import React, { useState, useEffect, useRef } from 'react'
import { Button, Form } from 'react-bootstrap'
import Select from 'react-select'
import { useAdminStripeAvailableCoupons } from 'hooks/use-admin-stripe'
import { STRIPE_PRIMARY } from 'constants/stripe'
import CouponManageModal from './coupon-manage-modal'

interface CouponManagerProps {
  onCouponsChange?: (coupons: any[]) => void
  initialCoupons?: any[]
}

const CouponManager: React.FC<CouponManagerProps> = ({
  onCouponsChange,
  initialCoupons = [],
}) => {
  const styles = {
    container: { marginTop: '16px' },
    addButton: {
      background: 'none',
      border: 'none',
      color: STRIPE_PRIMARY,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      padding: 0,
      textDecoration: 'none',
    },
    dropdownContainer: {
      marginTop: '12px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    dropdown: {
      width: '200px',
    },
    tableWrap: {
      marginTop: '8px',
      width: '100%',
      maxWidth: '320px',
      border: '0',
      borderRadius: '0',
      overflow: 'visible',
      background: 'transparent',
    } as React.CSSProperties,
    tableHeader: {
      display: 'none',
    },
    tableRow: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '8px',
      padding: '6px 0',
      alignItems: 'center',
      borderTop: '0',
    },
    rowCouponBox: {
      display: 'inline-block',
      border: 'none',
      borderRadius: '6px',
      backgroundColor: '#f8f9fa',
      padding: '6px 10px',
      maxWidth: '100%',
    },
    couponCode: {
      fontWeight: '500',
      fontSize: '14px',
      marginBottom: '2px',
      color: '#000000',
    },
    couponDescription: {
      color: '#6b7c93',
      fontSize: '12px',
    },
    rowActions: { display: 'flex', alignItems: 'center', gap: '8px' },
    removeButton: {
      background: 'none',
      border: 'none',
      color: '#e53d3d',
      cursor: 'pointer',
      padding: '2px 4px',
      fontSize: '16px',
      borderRadius: '4px',
    },
    manageButton: {
      background: 'none',
      border: 'none',
      color: STRIPE_PRIMARY,
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      padding: 0,
      textDecoration: 'none',
      flexShrink: 0,
    },
  }

  // States
  const [coupons, setCoupons] = useState<any[]>(() => initialCoupons)
  const [selectedCoupon, setSelectedCoupon] = useState<string>('')
  const [showDropdown, setShowDropdown] = useState(false)
  const hasInitialized = useRef(false)
  const [editingCouponId, setEditingCouponId] = useState<string | null>(null)
  const [editSelectedCoupon, setEditSelectedCoupon] = useState<string>('')
  const [showManageModal, setShowManageModal] = useState(false)

  // Get all available coupons
  const { data: availableCoupons = [], isLoading } =
    useAdminStripeAvailableCoupons()

  // Initialize with initial coupons and notify parent
  useEffect(() => {
    if (initialCoupons.length > 0 && !hasInitialized.current) {
      // Set the coupons state with initial coupons
      setCoupons(initialCoupons)
      // Notify parent if callback exists
      if (onCouponsChange) {
        onCouponsChange(initialCoupons)
      }
      hasInitialized.current = true
    }
  }, [initialCoupons, onCouponsChange])

  const couponOptions = availableCoupons.map((coupon: any) => {
    const label = coupon.name || coupon.id

    let description = ''

    if (coupon.percent_off) {
      description += `${coupon.percent_off}% off`
    } else if (coupon.amount_off) {
      description += `$${(coupon.amount_off / 100).toFixed(2)} off`
    }

    if (coupon.duration) {
      if (coupon.duration === 'forever') {
        description += ' - forever'
      } else if (coupon.duration === 'once') {
        description += ' - one time use'
      } else if (coupon.duration === 'repeating' && coupon.duration_in_months) {
        description += ` - for ${coupon.duration_in_months} month${
          coupon.duration_in_months > 1 ? 's' : ''
        }`
      } else {
        description += ` - ${coupon.duration}`
      }
    }

    if (coupon.valid === false) {
      description += ' - expired'
    }

    if (coupon.metadata?.description) {
      description += ` - ${coupon.metadata.description}`
    }

    return {
      value: coupon.id,
      label,
      description,
      metadata: {
        percent_off: coupon.percent_off,
        amount_off: coupon.amount_off,
        duration: coupon.duration,
        duration_in_months: coupon.duration_in_months,
        valid: coupon.valid,
        metadata: coupon.metadata,
        created: coupon.created,
        livemode: coupon.livemode,
      },
    }
  })

  // Update parent component with coupons
  useEffect(() => {
    onCouponsChange?.(coupons)
  }, [coupons, onCouponsChange])

  const handleAddCoupon = () => {
    if (!selectedCoupon) return

    const coupon = availableCoupons.find((c) => c.id === selectedCoupon)
    if (!coupon) return

    // Check if coupon already exists
    const existingCoupon = coupons.find((c) => c.id === selectedCoupon)
    if (existingCoupon) {
      // Coupon already exists, don't add duplicate
      return
    }

    const newCoupon = {
      id: coupon.id,
      name: coupon.name || coupon.id,
      status: 'active',
      couponDetails: coupon,
      percent_off: coupon.percent_off,
      amount_off: coupon.amount_off,
      duration: coupon.duration,
      duration_in_months: coupon.duration_in_months,
      valid: coupon.valid,
      metadata: coupon.metadata,
    }

    setCoupons((prev) => [...prev, newCoupon])

    // Reset form
    setSelectedCoupon('')
    setShowDropdown(false)
  }

  const removeCoupon = (couponId: string) => {
    setCoupons((prev) => prev.filter((coupon) => coupon.id !== couponId))
  }

  const handleStartEdit = (_couponId: string) => {
    // Open centralized manage modal instead of inline editor
    setEditingCouponId(null)
    setEditSelectedCoupon('')
    setShowDropdown(false)
    setShowManageModal(true)
  }

  const handleCancelEdit = () => {
    setEditingCouponId(null)
    setEditSelectedCoupon('')
  }

  const handleUpdateCoupon = () => {
    if (!editingCouponId || !editSelectedCoupon) return
    const coupon = availableCoupons.find(
      (c: any) => c.id === editSelectedCoupon
    )
    if (!coupon) return

    // If changing to an id that already exists elsewhere and it's not the same row, block duplicate
    const duplicate = coupons.some(
      (c) => c.id === coupon.id && c.id !== editingCouponId
    )
    if (duplicate) {
      // Skip update to avoid duplicates
      setEditingCouponId(null)
      setEditSelectedCoupon('')
      return
    }

    setCoupons((prev) =>
      prev.map((c) =>
        c.id === editingCouponId
          ? {
              id: coupon.id,
              name: coupon.name || coupon.id,
              status: 'active',
              couponDetails: coupon,
              percent_off: coupon.percent_off,
              amount_off: coupon.amount_off,
              duration: coupon.duration,
              duration_in_months: coupon.duration_in_months,
              valid: coupon.valid,
              metadata: coupon.metadata,
            }
          : c
      )
    )

    setEditingCouponId(null)
    setEditSelectedCoupon('')
  }

  if (isLoading) {
    return null
  }

  return (
    <>
      {/* Add Coupon Button - Show unless selecting */}
      {!showDropdown && !editingCouponId && coupons.length === 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <button
            type="button"
            onClick={() => setShowDropdown(true)}
            style={{ ...styles.addButton, width: 'auto' }}
          >
            + Add coupon
          </button>
        </div>
      )}

      {/* Coupon Dropdown */}
      {showDropdown && (
        <div style={styles.dropdownContainer}>
          <div style={styles.dropdown}>
            <Select
              options={couponOptions}
              value={couponOptions.find(
                (option) => option.value === selectedCoupon
              )}
              onChange={(option: any) => setSelectedCoupon(option?.value || '')}
              placeholder="Choose a coupon..."
              isClearable
              formatOptionLabel={(option: any, { context }: any) => {
                if (context === 'menu') {
                  return (
                    <div style={{ padding: '4px 0' }}>
                      <div
                        style={{
                          fontWeight: '500',
                          fontSize: '13px',
                          color: 'inherit',
                        }}
                      >
                        {option.label}
                      </div>
                      {option.description && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: 'inherit',
                            opacity: 0.8,
                            marginTop: '2px',
                            lineHeight: '1.3',
                          }}
                        >
                          {option.description}
                        </div>
                      )}
                    </div>
                  )
                }
                return option.label
              }}
              styles={{
                control: (base: any) => ({
                  ...base,
                  backgroundColor: '#ffffff',
                  borderColor: '#e6ebf1',
                  minHeight: '38px',
                  '&:hover': { borderColor: '#635bff' },
                }),
                menu: (base: any) => ({
                  ...base,
                  backgroundColor: '#ffffff',
                  border: '1px solid #e6ebf1',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  maxHeight: '300px',
                }),
                option: (base: any, state: any) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? '#635bff'
                    : state.isFocused
                    ? '#f8f9fa'
                    : '#ffffff',
                  color: state.isSelected ? '#ffffff' : '#32325d',
                  padding: '8px 12px',
                  fontSize: '13px',
                  lineHeight: '1.4',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  '&:hover': {
                    backgroundColor: state.isSelected ? '#635bff' : '#f8f9fa',
                  },
                }),
                singleValue: (base: any) => ({
                  ...base,
                  color: '#32325d',
                  fontSize: '13px',
                  lineHeight: '1.4',
                }),
                placeholder: (base: any) => ({
                  ...base,
                  color: '#6b7c93',
                  fontSize: '13px',
                }),
                input: (base: any) => ({
                  ...base,
                  color: '#32325d',
                  fontSize: '13px',
                }),
              }}
            />
          </div>
          <Button
            onClick={handleAddCoupon}
            disabled={!selectedCoupon}
            size="sm"
            style={{
              backgroundColor: STRIPE_PRIMARY,
              borderColor: STRIPE_PRIMARY,
            }}
          >
            Add
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setShowDropdown(false)
              setSelectedCoupon('')
            }}
          >
            Cancel
          </Button>
        </div>
      )}

      {/* Applied Coupons Section - Compact list */}
      {coupons.length > 0 && (
        <div style={styles.tableWrap}>
          {coupons.map((c, idx) => (
            <div
              key={c.id}
              style={{
                ...styles.tableRow,
                borderBottom:
                  idx === coupons.length - 1 ? 'none' : '2px solid #e6ebf1',
              }}
            >
              <div>
                <div style={styles.rowCouponBox}>
                  <div style={styles.couponCode}>{c.name}</div>
                  <div style={styles.couponDescription}>
                    {c.percent_off
                      ? `${c.percent_off}% off${
                          c.duration === 'forever' ? ' forever' : ''
                        }`
                      : c.amount_off
                      ? `$${(c.amount_off / 100).toFixed(2)} off`
                      : c.metadata?.description || 'Coupon applied'}
                  </div>
                </div>
              </div>
              <div style={styles.rowActions}>
                <button
                  type="button"
                  onClick={() => removeCoupon(c.id)}
                  style={styles.removeButton}
                  title="Remove coupon"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: '6px' }}>
            <button
              type="button"
              onClick={() => setShowManageModal(true)}
              style={styles.manageButton}
            >
              Manage coupons
            </button>
          </div>
        </div>
      )}

      <CouponManageModal
        show={showManageModal}
        onClose={() => setShowManageModal(false)}
        coupons={coupons}
        availableCoupons={availableCoupons}
        onSave={(nextCoupons) => {
          setCoupons(nextCoupons)
          setShowManageModal(false)
        }}
      />
    </>
  )
}

export default CouponManager
